import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Menu, X, ChevronDown } from 'lucide-react';
import logo from '@/assets/logo.png';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export const Header = () => {
  const { t } = useTranslation();
  const { user, signOut, isStaff } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  const initials = (user?.user_metadata?.full_name || user?.email || '?')
    .split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);

  useEffect(() => {
    if (!user) return;
    supabase.from('profiles').select('avatar_url').eq('id', user.id).single().then(({ data }) => {
      if (data?.avatar_url) setAvatarUrl(data.avatar_url);
    });
  }, [user]);

  const freeLinks = [
    { to: '/articles?category=texts', label: t('free.texts') },
    { to: '/articles?category=about_me', label: t('free.aboutMe') },
    { to: '/articles?category=mnemonics', label: t('free.mnemonics') },
    { to: '/articles?category=for_kids', label: t('free.forKids') },
    { to: '/daily-word', label: t('free.dailyWord') },
  ];

  return (
    <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-md">
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src={logo} alt="MnemoSpeak" className="h-8 w-auto" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-5 lg:flex">
          {/* Free content dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              {t('free.freeSection')}
              <ChevronDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              {freeLinks.map((link) => (
                <DropdownMenuItem key={link.to} onClick={() => navigate(link.to)}>
                  {link.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <>
              <Link to="/dashboard" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {t('common.dashboard')}
              </Link>
              <Link to="/courses" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {t('common.courses')}
              </Link>
              <Link to="/practice" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {t('common.practice')}
              </Link>
              <Link to="/learn-words" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {t('vocab.learnWords')}
              </Link>
              <Link to="/leaderboard" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {t('nav.leaderboard')}
              </Link>
              <Link to="/messages" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {t('nav.messages')}
              </Link>
              <Link to="/pricing" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {t('common.pricing')}
              </Link>
              <Link to="/resources" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {t('free.resources')}
              </Link>
            </>
          ) : (
            <>
              <Link to="/pricing" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {t('common.pricing')}
              </Link>
              <Link to="/resources" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {t('free.resources')}
              </Link>
            </>
          )}
          {/* <LanguageSwitcher /> — hidden from users */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="rounded-full ring-2 ring-transparent hover:ring-primary/30 transition-all" aria-label={t('common.menu', 'User menu')}>
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={avatarUrl || undefined} />
                    <AvatarFallback className="text-xs bg-primary/10 text-primary">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/dashboard')}>{t('common.dashboard')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profile')}>{t('nav.profile')}</DropdownMenuItem>
                {isStaff && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/admin')}>{t('nav.adminPanel')}</DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => signOut().then(() => navigate('/'))}>
                  {t('common.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>
                {t('common.login')}
              </Button>
              <Button size="sm" onClick={() => navigate('/register')}>
                {t('common.register')}
              </Button>
            </div>
          )}
        </nav>

        {/* Mobile toggle */}
        <button className="lg:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="border-t bg-background p-4 lg:hidden animate-fade-in">
          <nav className="flex flex-col gap-3">
            {/* Free links */}
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('free.freeSection')}</p>
            {freeLinks.map((link) => (
              <Link key={link.to} to={link.to} className="text-sm font-medium pl-2" onClick={() => setMobileOpen(false)}>
                {link.label}
              </Link>
            ))}
            <div className="border-t my-1" />

            {user ? (
              <>
                <Link to="/dashboard" className="text-sm font-medium" onClick={() => setMobileOpen(false)}>{t('common.dashboard')}</Link>
                <Link to="/courses" className="text-sm font-medium" onClick={() => setMobileOpen(false)}>{t('common.courses')}</Link>
                <Link to="/practice" className="text-sm font-medium" onClick={() => setMobileOpen(false)}>{t('common.practice')}</Link>
                <Link to="/learn-words" className="text-sm font-medium" onClick={() => setMobileOpen(false)}>{t('vocab.learnWords')}</Link>
                <Link to="/leaderboard" className="text-sm font-medium" onClick={() => setMobileOpen(false)}>{t('nav.leaderboard')}</Link>
                <Link to="/pricing" className="text-sm font-medium" onClick={() => setMobileOpen(false)}>{t('common.pricing')}</Link>
                <Link to="/resources" className="text-sm font-medium" onClick={() => setMobileOpen(false)}>{t('free.resources')}</Link>
                <Link to="/profile" className="text-sm font-medium" onClick={() => setMobileOpen(false)}>{t('nav.profile')}</Link>
                {isStaff && (
                  <Link to="/admin" className="text-sm font-medium text-primary" onClick={() => setMobileOpen(false)}>{t('nav.adminPanel')}</Link>
                )}
                <div className="border-t pt-2 mt-1">
                  <Button variant="ghost" size="sm" className="w-full justify-start" onClick={() => { signOut().then(() => navigate('/')); setMobileOpen(false); }}>
                    {t('common.logout')}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => { navigate('/login'); setMobileOpen(false); }}>{t('common.login')}</Button>
                <Button size="sm" onClick={() => { navigate('/register'); setMobileOpen(false); }}>{t('common.register')}</Button>
              </>
            )}
            {/* <LanguageSwitcher /> — hidden from users */}
          </nav>
        </div>
      )}
    </header>
  );
};
